import React, { useState } from 'react';
import { collectionsAPI } from '../../../lib/api';
import { formatCurrencyCompact } from '../../../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { Textarea } from '../../ui/textarea';
import { Skeleton } from '../../ui/skeleton';
import { ScrollArea } from '../../ui/scroll-area';
import {
  Clock, Phone, Mail, Users as UsersIcon, Scale, FileText,
  AlertTriangle, MessageSquarePlus, History
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-slate-100 text-slate-700' },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-700' },
  promised: { label: 'Promised', color: 'bg-emerald-100 text-emerald-700' },
  disputed: { label: 'Disputed', color: 'bg-amber-100 text-amber-700' },
  partial_payment: { label: 'Partial', color: 'bg-teal-100 text-teal-700' },
  escalated: { label: 'Escalated', color: 'bg-red-100 text-red-700' },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700' },
  written_off: { label: 'Written Off', color: 'bg-gray-100 text-gray-500' },
};

const ACTION_CONFIG = {
  call: { label: 'Call', icon: Phone },
  email: { label: 'Email', icon: Mail },
  meeting: { label: 'Meeting', icon: UsersIcon },
  escalate: { label: 'Escalate', icon: AlertTriangle },
  legal_notice: { label: 'Legal Notice', icon: Scale },
  note: { label: 'Note', icon: MessageSquarePlus },
};

export function CollectionDetailSheet({ detailSheet, setDetailSheet, collectors, actions, actionsLoading, onRefresh, setActions, currency }) {
  const [editStatus, setEditStatus] = useState(detailSheet?.collection_status || 'new');
  const [editAssignee, setEditAssignee] = useState(detailSheet?.assigned_to || '');
  const [editPromisedDate, setEditPromisedDate] = useState(detailSheet?.promised_date || '');
  const [editNotes, setEditNotes] = useState(detailSheet?.notes || '');
  const [actionType, setActionType] = useState('call');
  const [actionDesc, setActionDesc] = useState('');
  const [actionOutcome, setActionOutcome] = useState('');
  const [actionNextDate, setActionNextDate] = useState('');

  // Sync form state when sheet opens with new invoice
  React.useEffect(() => {
    if (detailSheet) {
      setEditStatus(detailSheet.collection_status || 'new');
      setEditAssignee(detailSheet.assigned_to || '');
      setEditPromisedDate(detailSheet.promised_date || '');
      setEditNotes(detailSheet.notes || '');
      setActionType('call');
      setActionDesc('');
      setActionOutcome('');
      setActionNextDate('');
    }
  }, [detailSheet?.invoice_number]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusUpdate = async () => {
    if (!detailSheet) return;
    try {
      await collectionsAPI.updateCollectionStatus(detailSheet.invoice_number, {
        status: editStatus,
        assigned_to: editAssignee,
        promised_date: editPromisedDate,
        notes: editNotes,
      });
      toast.success(`Status updated to "${STATUS_CONFIG[editStatus]?.label || editStatus}"`);
      onRefresh();
    } catch { toast.error('Failed to update status'); }
  };

  const handleAddAction = async () => {
    if (!detailSheet || !actionDesc.trim()) return;
    try {
      await collectionsAPI.addCollectionAction(detailSheet.invoice_number, {
        action_type: actionType,
        description: actionDesc,
        outcome: actionOutcome,
        next_action_date: actionNextDate,
      });
      toast.success('Follow-up action logged');
      setActionDesc('');
      setActionOutcome('');
      setActionNextDate('');
      const res = await collectionsAPI.getCollectionActions(detailSheet.invoice_number);
      setActions(res.data || []);
      onRefresh();
    } catch { toast.error('Failed to log action'); }
  };

  return (
    <Sheet open={!!detailSheet} onOpenChange={open => { if (!open) setDetailSheet(null); }}>
      <SheetContent className="w-[520px] sm:w-[600px] overflow-y-auto" data-testid="collection-detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {detailSheet?.invoice_number}
            <Badge
              className="text-[10px] font-bold ml-2"
              style={{ backgroundColor: detailSheet?.aging_color, color: '#fff' }}
            >
              {detailSheet?.aging_days}d overdue
            </Badge>
          </SheetTitle>
        </SheetHeader>

        {detailSheet && (
          <div className="mt-4 space-y-5">
            {/* Invoice summary */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 border">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Account</p>
                <p className="text-sm font-semibold mt-0.5">{detailSheet.account_name}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Salesperson</p>
                <p className="text-sm font-semibold mt-0.5">{detailSheet.salesperson || '-'}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="text-[10px] text-red-500 uppercase tracking-wider">Amount Due</p>
                <p className="text-lg font-bold text-red-700 mt-0.5">{formatCurrencyCompact(detailSheet.amount_residual, detailSheet.currency || currency)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Invoice Total</p>
                <p className="text-sm font-semibold mt-0.5">{formatCurrencyCompact(detailSheet.amount_total, detailSheet.currency || currency)}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-lg p-2.5 border text-center">
                <p className="text-[10px] text-gray-500">Due Date</p>
                <p className="text-xs font-semibold">{detailSheet.due_date}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5 border text-center">
                <p className="text-[10px] text-gray-500">Aging</p>
                <p className="text-xs font-bold" style={{ color: detailSheet.aging_color }}>{detailSheet.aging_days} days</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5 border text-center">
                <p className="text-[10px] text-gray-500">Payment</p>
                <p className="text-xs font-semibold">{detailSheet.payment_state}</p>
              </div>
            </div>

            {/* Collection Status Update */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> Collection Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase">Status</label>
                    <Select value={editStatus} onValueChange={setEditStatus}>
                      <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="edit-collection-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                          <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase">Assigned To</label>
                    <Select value={editAssignee || '_none'} onValueChange={v => setEditAssignee(v === '_none' ? '' : v)}>
                      <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="edit-collection-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Unassigned</SelectItem>
                        {collectors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase">Promised Date</label>
                    <Input type="date" value={editPromisedDate} onChange={e => setEditPromisedDate(e.target.value)} className="h-8 text-xs mt-0.5" data-testid="edit-promised-date" />
                  </div>
                  <div className="flex items-end">
                    <Button size="sm" className="h-8 w-full bg-[#800000] hover:bg-[#9a1919] text-white" onClick={handleStatusUpdate} data-testid="save-collection-status">Save Status</Button>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">Notes</label>
                  <Textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} className="mt-0.5 text-xs min-h-[50px]" placeholder="Add notes about this collection..." data-testid="edit-collection-notes" />
                </div>
              </CardContent>
            </Card>

            {/* Log Follow-up Action */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <MessageSquarePlus className="h-4 w-4" /> Log Follow-up Action
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase">Type</label>
                    <Select value={actionType} onValueChange={setActionType}>
                      <SelectTrigger className="h-8 text-xs mt-0.5" data-testid="action-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ACTION_CONFIG).map(([key, cfg]) => <SelectItem key={key} value={key}>{cfg.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 uppercase">Next Action Date</label>
                    <Input type="date" value={actionNextDate} onChange={e => setActionNextDate(e.target.value)} className="h-8 text-xs mt-0.5" data-testid="action-next-date" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">Description</label>
                  <Textarea value={actionDesc} onChange={e => setActionDesc(e.target.value)} placeholder="What did you do? E.g., Called finance dept..." className="mt-0.5 text-xs min-h-[40px]" data-testid="action-description" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">Outcome</label>
                  <Input value={actionOutcome} onChange={e => setActionOutcome(e.target.value)} placeholder="E.g., Promised to pay by end of month" className="h-8 text-xs mt-0.5" data-testid="action-outcome" />
                </div>
                <Button size="sm" className="w-full h-8 bg-[#800000] hover:bg-[#9a1919] text-white" disabled={!actionDesc.trim()} onClick={handleAddAction} data-testid="submit-action-btn">
                  <MessageSquarePlus className="h-3.5 w-3.5 mr-1" /> Log Action
                </Button>
              </CardContent>
            </Card>

            {/* Action History */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-1.5">
                  <History className="h-4 w-4" /> Action History
                  <Badge variant="secondary" className="text-[10px]">{actions.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {actionsLoading ? (
                  <div className="space-y-2"><Skeleton className="h-12" /><Skeleton className="h-12" /></div>
                ) : actions.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">No follow-up actions recorded yet</p>
                ) : (
                  <ScrollArea className="max-h-[250px]">
                    <div className="space-y-2">
                      {actions.map((action) => {
                        const cfg = ACTION_CONFIG[action.action_type] || ACTION_CONFIG.note;
                        const Icon = cfg.icon;
                        return (
                          <div key={action.id} className="flex gap-2.5 p-2.5 rounded-lg border bg-gray-50" data-testid={`action-${action.id}`}>
                            <div className="w-7 h-7 rounded-full bg-white border flex items-center justify-center shrink-0">
                              <Icon className="h-3.5 w-3.5 text-gray-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <Badge variant="outline" className="text-[9px]">{cfg.label}</Badge>
                                <span className="text-[10px] text-gray-400">by {action.created_by}</span>
                                <span className="text-[10px] text-gray-400 ml-auto">
                                  {action.created_at ? new Date(action.created_at).toLocaleDateString() : ''}
                                </span>
                              </div>
                              <p className="text-xs text-gray-700 mt-0.5">{action.description}</p>
                              {action.outcome && <p className="text-[10px] text-emerald-600 mt-0.5">Outcome: {action.outcome}</p>}
                              {action.next_action_date && <p className="text-[10px] text-blue-600 mt-0.5">Next: {action.next_action_date}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
